import axios from 'axios';

import { SNCDashboardCompareDateRange } from '../../../Enum/SNCDashboardCompareDateRange';
import { SNCDashboardMetricDateRange } from '../../../Enum/SNCDashboardMetricDateRange';
import { apiString as api } from '../../../utils/constants';

import ISNCDashboardDrillDown, {
    ISNCDashboardDailyPointSummary,
    ISNCDashboardRequestDetail,
} from '../../../Models/SNCDashboard/SNCDashboardDrillDown.model';

import { SNCDashboardDrillDownType } from '../../../Enum/SNCDashboardDrillDownType';

interface SNCDashboardProviderResponse {
    ProviderId: string;
    ProviderName: string;
}

interface SNCDashboardTeamResponse {
    TeamId: string;
    TeamName: string;
    CurrentPeriodCount: number;
    ComparePeriodCount: number;
    Difference: number;
    PercentageChange: number;
}

interface SNCDashboardStatusResponse {
    Status: string;
    CurrentPeriodCount: number;
    ComparePeriodCount: number;
}

interface SNCDashboardPointBreakdownResponse {
    RequestTypeId: string;
    RequestType: string;
    PointsPerTask: number;
    CompletedToday: number;
    PointsEarnedToday: number;
}

interface SNCDashboardResponse {
    TotalRequests: number;
    CompareTotalRequests: number;
    TotalRequestsPercentageChange: number;

    CompletedRequests: number;
    CompareCompletedRequests: number;
    CompletedPercentageChange: number;

    UpdatedRequests: number;
    CompareUpdatedRequests: number;
    UpdatedPercentageChange: number;

    DailyGoal: number;
    PointsEarned: number;
    DailyProgressPercentage: number;
    PointsRemaining: number;

    RequestsPerTeam: SNCDashboardTeamResponse[];
    RequestsPerStatus: SNCDashboardStatusResponse[];
    DailyPointBreakdown: SNCDashboardPointBreakdownResponse[];
    Providers: SNCDashboardProviderResponse[];
}

export interface GetSNCDashboardParams {
    compareDateRange: SNCDashboardCompareDateRange;
    completedDateRange: SNCDashboardMetricDateRange;
    updatedDateRange: SNCDashboardMetricDateRange;
    compareDateFrom?: string;
    compareDateTo?: string;
    teamId?: string;
    providerId?: string;
}

export interface ISNCDashboardDrillDownParams {
    drillDownType: SNCDashboardDrillDownType;
    compareDate?: string;
    metricDateRange: SNCDashboardMetricDateRange;
    teamId?: string;
    status?: string;
    providerId?: string;
}

interface SNCDashboardDailyPointSummaryResponse {
    DailyGoal: number;
    PointsEarned: number;
    PointsRemaining: number;
    ProgressPercentage: number;
    CompletedRequests: number;
    RequestUpdates: number;
}

interface SNCDashboardRequestDetailResponse {
    RequestId: string;
    MemberId: string | null;
    MemberName: string | null;
    AhcccsId: string | null;
    TeamId: string | null;
    TeamName: string | null;
    RequestType: string | null;
    Status: string | null;
    ProviderId: string | null;
    ProviderName: string | null;
    RequestCreatedOn: string | null;
    CompletedOn: string | null;
    LastUpdatedOn: string | null;
    UpdateCount: number;
    CompletionPoints: number;
    UpdatePoints: number;
    Points: number;
    CompletedInPeriod: boolean;
    UpdatedInPeriod: boolean;
}

interface SNCDashboardDrillDownResponse {
    Title: string;
    CurrentPeriodLabel: string;
    ComparePeriodLabel: string;
    CurrentPeriodFrom: string;
    CurrentPeriodTo: string;
    ComparePeriodFrom: string;
    ComparePeriodTo: string;
    CurrentPeriod?: SNCDashboardRequestDetailResponse[] | null;
    ComparePeriod?: SNCDashboardRequestDetailResponse[] | null;
    Providers?: SNCDashboardProviderResponse[] | null;
    DailyPointSummary?: SNCDashboardDailyPointSummaryResponse | null;
}

const endpoint = `${api}/sncdashboard`;

const mapSNCDashboard = (
    dashboard: SNCDashboardResponse
) => ({
    totalRequests: dashboard.TotalRequests,
    compareTotalRequests: dashboard.CompareTotalRequests,
    totalRequestsPercentageChange: dashboard.TotalRequestsPercentageChange,

    completedRequests: dashboard.CompletedRequests,
    compareCompletedRequests: dashboard.CompareCompletedRequests,
    completedPercentageChange: dashboard.CompletedPercentageChange,

    updatedRequests: dashboard.UpdatedRequests,
    compareUpdatedRequests: dashboard.CompareUpdatedRequests,
    updatedPercentageChange: dashboard.UpdatedPercentageChange,

    dailyGoal: dashboard.DailyGoal,
    pointsEarned: dashboard.PointsEarned,
    dailyProgressPercentage: dashboard.DailyProgressPercentage,
    pointsRemaining: dashboard.PointsRemaining,

    requestsPerTeam: (dashboard.RequestsPerTeam || []).map(
        (team) => ({
            teamId: team.TeamId,
            teamName: team.TeamName,
            currentPeriodCount: team.CurrentPeriodCount,
            comparePeriodCount: team.ComparePeriodCount,
            difference: team.Difference,
            percentageChange: team.PercentageChange,
        })
    ),

    requestsPerStatus: (dashboard.RequestsPerStatus || []).map(
        (status) => ({
            status: status.Status,
            currentPeriodCount: status.CurrentPeriodCount,
            comparePeriodCount: status.ComparePeriodCount,
        })
    ),

    dailyPointBreakdown: (dashboard.DailyPointBreakdown || []).map(
        (point) => ({
            requestTypeId: point.RequestTypeId,
            requestType: point.RequestType,
            pointsPerTask: point.PointsPerTask,
            completedToday: point.CompletedToday,
            pointsEarnedToday: point.PointsEarnedToday,
        })
    ),

    providers: (dashboard.Providers || []).map(
        (provider) => ({
            providerId: provider.ProviderId,
            providerName: provider.ProviderName,
        })
    ),
});

const mapDailyPointSummary = (
    summary: SNCDashboardDailyPointSummaryResponse | null | undefined
): ISNCDashboardDailyPointSummary | undefined => {
    if (!summary) {
        return undefined;
    }

    return {
        dailyGoal: summary.DailyGoal ?? 0,
        pointsEarned: summary.PointsEarned ?? 0,
        pointsRemaining: summary.PointsRemaining ?? 0,
        progressPercentage: summary.ProgressPercentage ?? 0,
        completedRequests: summary.CompletedRequests ?? 0,
        requestUpdates: summary.RequestUpdates ?? 0,
    };
};

const mapRequestDetail = (
    row: SNCDashboardRequestDetailResponse
): ISNCDashboardRequestDetail => ({
    requestId: row.RequestId ?? '',
    memberId: row.MemberId ?? undefined,
    memberName: row.MemberName ?? '',
    ahcccsId: row.AhcccsId ?? undefined,
    teamId: row.TeamId ?? undefined,
    teamName: row.TeamName ?? undefined,
    requestType: row.RequestType ?? undefined,
    status: row.Status ?? undefined,
    providerId: row.ProviderId ?? undefined,
    providerName: row.ProviderName ?? undefined,
    requestCreatedOn: row.RequestCreatedOn ?? undefined,
    completedOn: row.CompletedOn ?? undefined,
    lastUpdatedOn: row.LastUpdatedOn ?? undefined,
    updateCount: row.UpdateCount ?? 0,
    completionPoints: row.CompletionPoints ?? 0,
    updatePoints: row.UpdatePoints ?? 0,
    points: row.Points ?? 0,
    completedInPeriod: row.CompletedInPeriod ?? false,
    updatedInPeriod: row.UpdatedInPeriod ?? false,
});

const mapDrillDown = (
    data: SNCDashboardDrillDownResponse
): ISNCDashboardDrillDown => {
    if (
        !data ||
        typeof data !== 'object' ||
        typeof data.Title !== 'string' ||
        typeof data.CurrentPeriodLabel !== 'string' ||
        typeof data.ComparePeriodLabel !== 'string' ||
        typeof data.CurrentPeriodFrom !== 'string' ||
        typeof data.CurrentPeriodTo !== 'string' ||
        typeof data.ComparePeriodFrom !== 'string' ||
        typeof data.ComparePeriodTo !== 'string' ||
        (
            data.CurrentPeriod != null &&
            !Array.isArray(data.CurrentPeriod)
        ) ||
        (
            data.ComparePeriod != null &&
            !Array.isArray(data.ComparePeriod)
        ) ||
        (
            data.Providers != null &&
            !Array.isArray(data.Providers)
        ) ||
        (
            data.DailyPointSummary != null &&
            (typeof data.DailyPointSummary !== 'object' ||
                Array.isArray(data.DailyPointSummary))
        )
    ) {
        throw new Error(
            'Invalid SNC drill-down response.'
        );
    }

    return {
        title:
            data.Title,

        currentPeriodLabel:
            data.CurrentPeriodLabel,

        comparePeriodLabel:
            data.ComparePeriodLabel,

        currentPeriodFrom:
            data.CurrentPeriodFrom,

        currentPeriodTo:
            data.CurrentPeriodTo,

        comparePeriodFrom:
            data.ComparePeriodFrom,

        comparePeriodTo:
            data.ComparePeriodTo,

        currentPeriod:
            (
                data.CurrentPeriod ??
                []
            ).map(
                mapRequestDetail
            ),

        comparePeriod:
            (
                data.ComparePeriod ??
                []
            ).map(
                mapRequestDetail
            ),

        providers:
            (
                data.Providers ??
                []
            ).map(
                provider => ({
                    providerId:
                        provider.ProviderId,

                    providerName:
                        provider.ProviderName,
                })
            ),

        dailyPointSummary:
            mapDailyPointSummary(
                data.DailyPointSummary
            ),
    };
};

export const getSNCDashboard = async (
    params: GetSNCDashboardParams
) => {
    const query = new URLSearchParams({
        compareDateRange: String(
            params.compareDateRange
        ),

        completedDateRange: String(
            params.completedDateRange
        ),

        updatedDateRange: String(
            params.updatedDateRange
        ),
    });

    if (params.compareDateFrom) {
        query.append(
            'compareDateFrom',
            params.compareDateFrom
        );
    }

    if (params.compareDateTo) {
        query.append(
            'compareDateTo',
            params.compareDateTo
        );
    }

    if (
        params.teamId &&
        params.teamId !== 'All'
    ) {
        query.append(
            'teamId',
            params.teamId
        );
    }

    if (
        params.providerId &&
        params.providerId !== 'All'
    ) {
        query.append(
            'providerId',
            params.providerId
        );
    }

    const response =
        await axios.get<SNCDashboardResponse>(
            `${endpoint}/getsncdashboard?${query.toString()}`
        );

    return mapSNCDashboard(
        response.data
    );
};

export const getSNCDashboardDrillDown = async (
    params: ISNCDashboardDrillDownParams
): Promise<ISNCDashboardDrillDown> => {
    const query =
        new URLSearchParams({
            drillDownType:
                String(
                    params.drillDownType
                ),

            metricDateRange:
                String(
                    params.metricDateRange
                ),
        });

    if (
        params.compareDate
    ) {
        query.append(
            'compareDate',
            params.compareDate
        );
    }

    if (
        params.teamId &&
        params.teamId !==
        'All'
    ) {
        query.append(
            'teamId',
            params.teamId
        );
    }

    if (
        params.providerId &&
        params.providerId !==
        'All'
    ) {
        query.append(
            'providerId',
            params.providerId
        );
    }

    if (
        params.status &&
        params.status !==
        'All'
    ) {
        query.append(
            'status',
            params.status
        );
    }

    const response =
        await axios.get<SNCDashboardDrillDownResponse>(
            `${endpoint}/getsncdashboarddetail?${query.toString()}`
        );

    return mapDrillDown(
        response.data
    );
};

export const exportSNCDashboardDrillDown = async (
    params: ISNCDashboardDrillDownParams
) => {
    const query =
        new URLSearchParams({
            drillDownType:
                String(
                    params.drillDownType
                ),

            metricDateRange:
                String(
                    params.metricDateRange
                ),
        });

    if (
        params.compareDate
    ) {
        query.append(
            'compareDate',
            params.compareDate
        );
    }

    if (
        params.teamId &&
        params.teamId !==
        'All'
    ) {
        query.append(
            'teamId',
            params.teamId
        );
    }

    if (
        params.providerId &&
        params.providerId !==
        'All'
    ) {
        query.append(
            'providerId',
            params.providerId
        );
    }

    if (
        params.status &&
        params.status !==
        'All'
    ) {
        query.append(
            'status',
            params.status
        );
    }

    const response =
        await axios.get(
            `${endpoint}/exportsncdashboarddetails?${query.toString()}`,
            {
                responseType:
                    'blob',
            }
        );

    const contentDisposition =
        response.headers[
        'content-disposition'
        ];

    let fileName =
        'SNC_DrillDown.xlsx';

    if (
        contentDisposition
    ) {
        const utf8Match =
            contentDisposition.match(
                /filename\*=UTF-8''([^;]+)/i
            );

        const normalMatch =
            contentDisposition.match(
                /filename="?([^";]+)"?/i
            );

        if (
            utf8Match?.[1]
        ) {
            fileName =
                decodeURIComponent(
                    utf8Match[1]
                );
        }
        else if (
            normalMatch?.[1]
        ) {
            fileName =
                normalMatch[1];
        }
    }

    const url =
        window.URL.createObjectURL(
            new Blob([
                response.data,
            ])
        );

    const anchor =
        document.createElement(
            'a'
        );

    anchor.href =
        url;

    anchor.download =
        fileName;

    document.body.appendChild(
        anchor
    );

    anchor.click();

    anchor.remove();

    window.URL.revokeObjectURL(
        url
    );
};